import React, { useEffect, useState } from "react";
import {
  buildFacet,
  buildFieldSortCriterion,
  buildNumericFacet,
  buildNumericFilter,
  buildPager,
  buildQuerySummary,
  buildRelevanceSortCriterion,
  buildResultList,
  buildResultsPerPage,
  buildSearchBox,
  buildSort,
  Facet,
  loadQueryActions,
  NumericFacet,
  NumericFilter,
  Pager,
  QuerySummary,
  ResultList,
  SearchBox,
  SearchEngine,
  Sort,
  SortCriterion,
  SortOrder,
} from "@coveo/headless";
import { createContext, useContext } from "react";
import { createSearchEngine } from "../../../../coveo/utils/createEngine";
import { ecommerceFields } from "../../../../coveo/utils/ecommerceFields";
import { initializeUrlManager } from "../../../../coveo/utils/initializeUrlManager";
import { RegionContext } from "./RegionProvider";
import { ContinentContext } from "./ContinentProvider";

export enum EngineType {
  Search = "searchEngine",
}

interface EngineContextType {
  [EngineType.Search]: SearchEngine;
}

export const EngineContext = createContext<EngineContextType | null>(null);

export const useEngineContext = <T extends EngineType>(
  engine: T,
): EngineContextType[T] => {
  const context = useContext(EngineContext);

  if (!context) {
    throw new Error(`useEngineContext must be used within a EngineProvider`);
  }

  return context[engine];
};

export enum ControllerType {
  SearchBox = "searchBoxController",
  Sort = "sortController",
  Criteria = "criteria",
  CategoryFacet = "categoryFacetController",
  PriceFilter = "priceFilterController",
  Pager = "pagerController",
  ResultList = "resultListController",
  QuerySummary = "querySummaryController",
  BrandFacet = "brandFacetController",
  PriceFacet = "priceFacetController",
}

interface ControllerContextType {
  [ControllerType.SearchBox]: SearchBox;
  [ControllerType.Sort]: Sort;
  [ControllerType.Criteria]: [string, SortCriterion][];
  [ControllerType.CategoryFacet]: Facet;
  [ControllerType.PriceFilter]: NumericFilter;
  [ControllerType.Pager]: Pager;
  [ControllerType.ResultList]: ResultList;
  [ControllerType.QuerySummary]: QuerySummary;
  [ControllerType.BrandFacet]: Facet;
  [ControllerType.PriceFacet]: NumericFacet;
}

export const ControllerContext = createContext<ControllerContextType | null>(
  null,
);

export const useControllerContext = <T extends ControllerType>(
  controller: T,
): ControllerContextType[T] => {
  const context = useContext(ControllerContext);

  if (!context) {
    throw new Error(
      `useControllerContext must be used within a ControllerProvider`,
    );
  }

  return context[controller];
};

const PART_FIELDS = [
  "part_number",
  "local_price",
  "shipment_delays",
  "part_status",
  "entity_id",
  "featured_part_order",
  "new_part_order",
  "admin_only",
  "hide_price",
];

const HOME_SORT_CRITERIA =
  "@featured_part_order ascending, @new_part_order ascending";

const INITIAL_NUMBER_OF_RESULTS = 20;

interface EngineAndControllerProviderProps {
  accessToken: string;
  organizationId: string;
  shipmentSiteKey: number;
  children: any;
}

export const EngineAndControllerProvider: React.FC<
  EngineAndControllerProviderProps
> = (props) => {
  const region = useContext(RegionContext);
  const continentCode = useContext(ContinentContext);

  const [engines, setEngines] = useState<EngineContextType | null>(null);
  const [controllers, setControllers] = useState<ControllerContextType | null>(
    null,
  );

  useEffect(() => {
    const standAloneSearchBoxData = localStorage.getItem(
      "coveo_standalone_search_box_data",
    );

    const analytics = standAloneSearchBoxData
      ? { analytics: { originLevel3: document.referrer } }
      : {};

    const searchEngine = createSearchEngine({
      configuration: {
        organizationId: props.organizationId,
        accessToken: props.accessToken,
        search: { pipeline: "Parts" },
        ...analytics,
      },
      searchHub: "PartLibrarySearch",
      preprocessRequestBody: (body: any) => {
        const baseRequest = {
          ...body,
          fieldsToInclude: [...ecommerceFields, ...PART_FIELDS],
          dictionaryFieldContext: {
            local_price: region,
            shipment_delays: props.shipmentSiteKey.toString(),
          },
          context: {
            continent: continentCode,
          },
        };

        // On homepage (no search), prioritize featured and new parts in the results
        // This ensures they appear in the results if they exist
        if (!body.q || body.q === "") {
          return {
            ...baseRequest,
            sortCriteria: HOME_SORT_CRITERIA,
          };
        }

        return baseRequest;
      },
    });

    const searchBoxController = buildSearchBox(searchEngine);

    const criteria: [string, SortCriterion][] = [
      ["Relevance", buildRelevanceSortCriterion()],
      [
        "Price (Ascending)",
        buildFieldSortCriterion("local_price", SortOrder.Ascending),
      ],
      [
        "Price (Descending)",
        buildFieldSortCriterion("local_price", SortOrder.Descending),
      ],
    ];
    const initialCriterion = criteria[0][1];

    const sortController = buildSort(searchEngine, {
      initialState: { criterion: initialCriterion },
    });

    const brandFacetController = buildFacet(searchEngine, {
      options: { field: "ec_brand", numberOfValues: 999 },
    });

    const categoryFacetController = buildFacet(searchEngine, {
      // `injectionDepth` determines the maximum number of results to scan in the index to ensure that the facet lists
      // all potential facet values.
      options: {
        field: "ec_category",
        numberOfValues: 999,
        injectionDepth: 9999,
      },
    });
    const priceFilterController = buildNumericFilter(searchEngine, {
      options: { field: "local_price" },
    });
    const numericFacetController = buildNumericFacet(searchEngine, {
      options: { field: "local_price", generateAutomaticRanges: true },
    });

    buildResultsPerPage(searchEngine, {
      initialState: { numberOfResults: INITIAL_NUMBER_OF_RESULTS },
    });
    const pagerController = buildPager(searchEngine);
    const resultListController = buildResultList(searchEngine);
    const querySummaryController = buildQuerySummary(searchEngine);

    const { updateQuery } = loadQueryActions(searchEngine);

    const synchronizeWithUrl = initializeUrlManager(searchEngine);

    if (standAloneSearchBoxData) {
      localStorage.removeItem("coveo_standalone_search_box_data");
      const { value, analytics } = JSON.parse(standAloneSearchBoxData);
      searchEngine.dispatch(updateQuery({ q: value }));
      searchEngine.executeFirstSearchAfterStandaloneSearchBoxRedirect(
        analytics,
      );
    } else {
      synchronizeWithUrl();
      searchEngine.executeFirstSearch();
    }

    setEngines({
      [EngineType.Search]: searchEngine,
    });

    setControllers({
      [ControllerType.SearchBox]: searchBoxController,
      [ControllerType.Sort]: sortController,
      [ControllerType.Criteria]: criteria,
      [ControllerType.CategoryFacet]: categoryFacetController,
      [ControllerType.PriceFilter]: priceFilterController,
      [ControllerType.Pager]: pagerController,
      [ControllerType.ResultList]: resultListController,
      [ControllerType.QuerySummary]: querySummaryController,
      [ControllerType.BrandFacet]: brandFacetController,
      [ControllerType.PriceFacet]: numericFacetController,
    });
  }, []);

  if (engines === null || controllers === null) {
    return null;
  }

  return (
    <EngineContext.Provider value={engines}>
      <ControllerContext.Provider value={controllers}>
        {props.children}
      </ControllerContext.Provider>
    </EngineContext.Provider>
  );
};
